import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, MapPin, Users, TrendingUp, Filter } from 'lucide-react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import PlacementMap from './PlacementMap';

const US_TOPO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const ELIGIBILITY_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vQeYvTz_hHAnJWQW2KH8rfWNfS4XO7IkJYXIIoYUzdmmJAIR3WEVmesUnsL0EhJBAQj3OIMOMLXBayB/pub?gid=0&single=true&output=csv';

const SUPERVISOR_URL = 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRV5sBcK5c4vK1oK-ktrNi6rEizdVEXskItHECri7x3oOtrlVW-4oC5x1C7yACnQ5UuMahZe_cNHpI5/pub?gid=0&single=true&output=csv';

// US States with approximate center coordinates for the map
const STATE_GEO = {
  'Alabama': { abbr: 'AL', lat: 32.806671, lng: -86.791130 },
  'Alaska': { abbr: 'AK', lat: 61.370716, lng: -152.404419 },
  'Arizona': { abbr: 'AZ', lat: 33.729759, lng: -111.431221 },
  'Arkansas': { abbr: 'AR', lat: 34.969704, lng: -92.373123 },
  'California': { abbr: 'CA', lat: 36.116203, lng: -119.681564 },
  'Colorado': { abbr: 'CO', lat: 39.059811, lng: -105.311104 },
  'Connecticut': { abbr: 'CT', lat: 41.597782, lng: -72.755371 },
  'Delaware': { abbr: 'DE', lat: 39.318523, lng: -75.507141 },
  'Florida': { abbr: 'FL', lat: 27.766279, lng: -81.686783 },
  'Georgia': { abbr: 'GA', lat: 33.040619, lng: -83.643074 },
  'Hawaii': { abbr: 'HI', lat: 21.094318, lng: -157.498337 },
  'Idaho': { abbr: 'ID', lat: 44.240459, lng: -114.478828 },
  'Illinois': { abbr: 'IL', lat: 40.349457, lng: -88.986137 },
  'Indiana': { abbr: 'IN', lat: 39.849426, lng: -86.258278 },
  'Iowa': { abbr: 'IA', lat: 42.011539, lng: -93.210526 },
  'Kansas': { abbr: 'KS', lat: 38.526600, lng: -96.726486 },
  'Kentucky': { abbr: 'KY', lat: 37.668140, lng: -84.670067 },
  'Louisiana': { abbr: 'LA', lat: 31.169546, lng: -91.867805 },
  'Maine': { abbr: 'ME', lat: 44.693947, lng: -69.381927 },
  'Maryland': { abbr: 'MD', lat: 39.063946, lng: -76.802101 },
  'Massachusetts': { abbr: 'MA', lat: 42.230171, lng: -71.530106 },
  'Michigan': { abbr: 'MI', lat: 43.326618, lng: -84.536095 },
  'Minnesota': { abbr: 'MN', lat: 45.694454, lng: -93.900192 },
  'Mississippi': { abbr: 'MS', lat: 32.741646, lng: -89.678696 },
  'Missouri': { abbr: 'MO', lat: 38.456085, lng: -92.288368 },
  'Montana': { abbr: 'MT', lat: 46.921925, lng: -110.454353 },
  'Nebraska': { abbr: 'NE', lat: 41.125370, lng: -98.268082 },
  'Nevada': { abbr: 'NV', lat: 38.313515, lng: -117.055374 },
  'New Hampshire': { abbr: 'NH', lat: 43.452492, lng: -71.563896 },
  'New Jersey': { abbr: 'NJ', lat: 40.298904, lng: -74.521011 },
  'New Mexico': { abbr: 'NM', lat: 34.840515, lng: -106.248482 },
  'New York': { abbr: 'NY', lat: 42.165726, lng: -74.948051 },
  'North Carolina': { abbr: 'NC', lat: 35.630066, lng: -79.806419 },
  'North Dakota': { abbr: 'ND', lat: 47.528912, lng: -99.784012 },
  'Ohio': { abbr: 'OH', lat: 40.388783, lng: -82.764915 },
  'Oklahoma': { abbr: 'OK', lat: 35.565342, lng: -96.928917 },
  'Oregon': { abbr: 'OR', lat: 44.572021, lng: -122.070938 },
  'Pennsylvania': { abbr: 'PA', lat: 40.590752, lng: -77.209755 },
  'Rhode Island': { abbr: 'RI', lat: 41.680893, lng: -71.511780 },
  'South Carolina': { abbr: 'SC', lat: 33.856892, lng: -80.945007 },
  'South Dakota': { abbr: 'SD', lat: 44.299782, lng: -99.438828 },
  'Tennessee': { abbr: 'TN', lat: 35.747845, lng: -86.692345 },
  'Texas': { abbr: 'TX', lat: 31.054487, lng: -97.563461 },
  'Utah': { abbr: 'UT', lat: 40.150032, lng: -111.862434 },
  'Vermont': { abbr: 'VT', lat: 44.045876, lng: -72.710686 },
  'Virginia': { abbr: 'VA', lat: 37.769337, lng: -78.169968 },
  'Washington': { abbr: 'WA', lat: 47.400902, lng: -121.490494 },
  'West Virginia': { abbr: 'WV', lat: 38.491226, lng: -80.954456 },
  'Wisconsin': { abbr: 'WI', lat: 44.268543, lng: -89.616508 },
  'Wyoming': { abbr: 'WY', lat: 42.755966, lng: -107.302490 },
  'District of Columbia': { abbr: 'DC', lat: 38.897438, lng: -77.026817 },
};

// Texas metro areas for city-level geocoding
const TEXAS_METROS = {
  'dallas': { lat: 32.7767, lng: -96.7970, name: 'Dallas' },
  'fort worth': { lat: 32.7555, lng: -97.3308, name: 'Fort Worth' },
  'houston': { lat: 29.7604, lng: -95.3698, name: 'Houston' },
  'san antonio': { lat: 29.4241, lng: -98.4936, name: 'San Antonio' },
  'austin': { lat: 30.2672, lng: -97.7431, name: 'Austin' },
  'el paso': { lat: 31.7619, lng: -106.4850, name: 'El Paso' },
  'arlington': { lat: 32.7357, lng: -97.1081, name: 'Arlington' },
  'corpus christi': { lat: 27.8006, lng: -97.3964, name: 'Corpus Christi' },
  'plano': { lat: 33.0198, lng: -96.6989, name: 'Plano' },
  'lubbock': { lat: 33.5779, lng: -101.8552, name: 'Lubbock' },
  'irving': { lat: 32.8140, lng: -96.9489, name: 'Irving' },
  'garland': { lat: 32.9126, lng: -96.6389, name: 'Garland' },
  'frisco': { lat: 33.1507, lng: -96.8236, name: 'Frisco' },
  'mckinney': { lat: 33.1972, lng: -96.6397, name: 'McKinney' },
  'brownsville': { lat: 25.9017, lng: -97.4975, name: 'Brownsville' },
  'killeen': { lat: 31.1171, lng: -97.7278, name: 'Killeen' },
  'midland': { lat: 31.9973, lng: -102.0779, name: 'Midland' },
  'beaumont': { lat: 30.0802, lng: -94.1266, name: 'Beaumont' },
  'waco': { lat: 31.5493, lng: -97.1467, name: 'Waco' },
  'abilene': { lat: 32.4487, lng: -99.7331, name: 'Abilene' },
  'odessa': { lat: 31.8457, lng: -102.3676, name: 'Odessa' },
  'round rock': { lat: 30.5083, lng: -97.6789, name: 'Round Rock' },
  'richardson': { lat: 32.9483, lng: -96.7299, name: 'Richardson' },
  'tyler': { lat: 32.3513, lng: -95.3011, name: 'Tyler' },
  'amarillo': { lat: 35.2220, lng: -101.8313, name: 'Amarillo' },
  'grand prairie': { lat: 32.7459, lng: -96.9978, name: 'Grand Prairie' },
  'mesquite': { lat: 32.7668, lng: -96.5992, name: 'Mesquite' },
  'denton': { lat: 33.2148, lng: -97.1331, name: 'Denton' },
  'wichita falls': { lat: 33.9137, lng: -98.4934, name: 'Wichita Falls' },
  'cedar park': { lat: 30.5052, lng: -97.8203, name: 'Cedar Park' },
  'lewisville': { lat: 33.0462, lng: -96.9942, name: 'Lewisville' },
  'temple': { lat: 31.0982, lng: -97.3428, name: 'Temple' },
  'pflugerville': { lat: 30.4394, lng: -97.6200, name: 'Pflugerville' },
  'desoto': { lat: 32.5899, lng: -96.8570, name: 'DeSoto' },
  'mansfield': { lat: 32.5632, lng: -97.1417, name: 'Mansfield' },
  'cedar hill': { lat: 32.5885, lng: -96.9561, name: 'Cedar Hill' },
  'conroe': { lat: 30.3119, lng: -95.4561, name: 'Conroe' },
  'san marcos': { lat: 29.8833, lng: -97.9414, name: 'San Marcos' },
  'new braunfels': { lat: 29.7030, lng: -98.1245, name: 'New Braunfels' },
  'longview': { lat: 32.5007, lng: -94.7405, name: 'Longview' },
  'college station': { lat: 30.6280, lng: -96.3344, name: 'College Station' },
  'baytown': { lat: 29.7355, lng: -94.9774, name: 'Baytown' },
  'sugar land': { lat: 29.6197, lng: -95.6349, name: 'Sugar Land' },
  'pharr': { lat: 26.1948, lng: -98.1836, name: 'Pharr' },
  'keller': { lat: 32.9346, lng: -97.2520, name: 'Keller' },
  'spring': { lat: 30.0799, lng: -95.4172, name: 'Spring' },
  'cypress': { lat: 29.9691, lng: -95.6970, name: 'Cypress' },
  'humble': { lat: 29.9988, lng: -95.2622, name: 'Humble' },
  'pearland': { lat: 29.5636, lng: -95.2860, name: 'Pearland' },
  'league city': { lat: 29.5075, lng: -95.0950, name: 'League City' },
  'mission': { lat: 26.2159, lng: -98.3253, name: 'Mission' },
  'edinburg': { lat: 26.3017, lng: -98.1633, name: 'Edinburg' },
  'allen': { lat: 33.1032, lng: -96.6706, name: 'Allen' },
  'san angelo': { lat: 31.4638, lng: -100.4370, name: 'San Angelo' },
  'duncanville': { lat: 32.6518, lng: -96.9085, name: 'Duncanville' },
  'belton': { lat: 31.0557, lng: -97.4642, name: 'Belton' },
  'buda': { lat: 30.0852, lng: -97.8392, name: 'Buda' },
  'kyle': { lat: 29.9889, lng: -97.8772, name: 'Kyle' },
  'sherman': { lat: 33.6357, lng: -96.6089, name: 'Sherman' },
  'weatherford': { lat: 32.7593, lng: -97.7973, name: 'Weatherford' },
  'burleson': { lat: 32.5421, lng: -97.3209, name: 'Burleson' },
  'forney': { lat: 32.7482, lng: -96.4719, name: 'Forney' },
  'anna': { lat: 33.3490, lng: -96.5487, name: 'Anna' },
  'seagoville': { lat: 32.6360, lng: -96.5339, name: 'Seagoville' },
  'georgetown': { lat: 30.6333, lng: -97.6781, name: 'Georgetown' },
  'liberty': { lat: 30.0580, lng: -94.7955, name: 'Liberty' },
  'kingwood': { lat: 30.0444, lng: -95.2516, name: 'Kingwood' },
  'katy': { lat: 29.7858, lng: -95.8245, name: 'Katy' },
  'tomball': { lat: 30.0972, lng: -95.6161, name: 'Tomball' },
  'rowlett': { lat: 32.9029, lng: -96.5636, name: 'Rowlett' },
  'wylie': { lat: 33.0151, lng: -96.5389, name: 'Wylie' },
  'rockwall': { lat: 32.9312, lng: -96.4597, name: 'Rockwall' },
  'prosper': { lat: 33.2362, lng: -96.8011, name: 'Prosper' },
  'celina': { lat: 33.3246, lng: -96.7847, name: 'Celina' },
  'southlake': { lat: 32.9413, lng: -97.1342, name: 'Southlake' },
  'grapevine': { lat: 32.9343, lng: -97.0781, name: 'Grapevine' },
  'colleyville': { lat: 32.8810, lng: -97.1550, name: 'Colleyville' },
  'flower mound': { lat: 33.0146, lng: -97.0970, name: 'Flower Mound' },
  'coppell': { lat: 32.9546, lng: -97.0150, name: 'Coppell' },
  'the colony': { lat: 33.0890, lng: -96.8917, name: 'The Colony' },
  'little elm': { lat: 33.1626, lng: -96.9376, name: 'Little Elm' },
  'murphy': { lat: 33.0151, lng: -96.6128, name: 'Murphy' },
  'sachse': { lat: 32.9762, lng: -96.5953, name: 'Sachse' },
  'lancaster': { lat: 32.5921, lng: -96.7561, name: 'Lancaster' },
  'waxahachie': { lat: 32.3866, lng: -96.8483, name: 'Waxahachie' },
  'ennis': { lat: 32.3293, lng: -96.6252, name: 'Ennis' },
  'corsicana': { lat: 32.0954, lng: -96.4689, name: 'Corsicana' },
  'terrell': { lat: 32.7360, lng: -96.2753, name: 'Terrell' },
  'greenville': { lat: 33.1384, lng: -96.1108, name: 'Greenville' },
  'kaufman': { lat: 32.5890, lng: -96.3086, name: 'Kaufman' },
  'cleburne': { lat: 32.3476, lng: -97.3867, name: 'Cleburne' },
  'stephenville': { lat: 32.2207, lng: -98.2023, name: 'Stephenville' },
  'granbury': { lat: 32.4419, lng: -97.7942, name: 'Granbury' },
  'azle': { lat: 32.8951, lng: -97.5456, name: 'Azle' },
  'white settlement': { lat: 32.7596, lng: -97.4587, name: 'White Settlement' },
  'north richland hills': { lat: 32.8343, lng: -97.2289, name: 'North Richland Hills' },
  'hurst': { lat: 32.8235, lng: -97.1706, name: 'Hurst' },
  'euless': { lat: 32.8371, lng: -97.0820, name: 'Euless' },
  'bedford': { lat: 32.8440, lng: -97.1431, name: 'Bedford' },
  'haltom city': { lat: 32.7996, lng: -97.2692, name: 'Haltom City' },
  'crowley': { lat: 32.5793, lng: -97.3625, name: 'Crowley' },
  'kennedale': { lat: 32.6471, lng: -97.2259, name: 'Kennedale' },
  'benbrook': { lat: 32.6732, lng: -97.4606, name: 'Benbrook' },
  'lake worth': { lat: 32.8054, lng: -97.4431, name: 'Lake Worth' },
  'saginaw': { lat: 32.8601, lng: -97.3639, name: 'Saginaw' },
  'bossier city': { lat: 32.5160, lng: -93.7321, name: 'Bossier City' },
  'basile': { lat: 30.4863, lng: -92.6013, name: 'Basile' },
  'nacogdoches': { lat: 31.6035, lng: -94.6555, name: 'Nacogdoches' },
  'lufkin': { lat: 31.3382, lng: -94.7291, name: 'Lufkin' },
  'victoria': { lat: 28.8053, lng: -97.0036, name: 'Victoria' },
  'laredo': { lat: 27.5036, lng: -99.5076, name: 'Laredo' },
  'mcallen': { lat: 26.2034, lng: -98.2300, name: 'McAllen' },
  'harlingen': { lat: 26.1906, lng: -97.6961, name: 'Harlingen' },
};

// Major cities for other states
const US_OTHER_CITIES = {
  // Louisiana
  'baton rouge': { lat: 30.4515, lng: -91.1871, name: 'Baton Rouge' },
  'new orleans': { lat: 29.9511, lng: -90.0715, name: 'New Orleans' },
  'shreveport': { lat: 32.5252, lng: -93.7502, name: 'Shreveport' },
  'lafayette': { lat: 30.2241, lng: -92.0198, name: 'Lafayette' },
  'lake charles': { lat: 30.2266, lng: -93.2174, name: 'Lake Charles' },
  'monroe': { lat: 32.5093, lng: -92.1193, name: 'Monroe' },
  'alexandria': { lat: 31.3113, lng: -92.4451, name: 'Alexandria' },
  'houma': { lat: 29.5958, lng: -90.7195, name: 'Houma' },
  'kenner': { lat: 29.9941, lng: -90.2417, name: 'Kenner' },
  'bossier city': { lat: 32.5160, lng: -93.7321, name: 'Bossier City' },
  'basile': { lat: 30.4863, lng: -92.6013, name: 'Basile' },
  'opelousas': { lat: 30.5335, lng: -92.0815, name: 'Opelousas' },
  'ruston': { lat: 32.5232, lng: -92.6379, name: 'Ruston' },
  'sulphur': { lat: 30.2366, lng: -93.3774, name: 'Sulphur' },
  'hammond': { lat: 30.5044, lng: -90.4612, name: 'Hammond' },
  'slidell': { lat: 30.2752, lng: -89.7812, name: 'Slidell' },
  'gonzales': { lat: 30.2388, lng: -90.9201, name: 'Gonzales' },
  'zachary': { lat: 30.6485, lng: -91.1565, name: 'Zachary' },
  'haynesville': { lat: 32.9626, lng: -93.1401, name: 'Haynesville' },
  'natchitoches': { lat: 31.7607, lng: -93.0863, name: 'Natchitoches' },
  'denham springs': { lat: 30.4869, lng: -90.9568, name: 'Denham Springs' },
  'thibodaux': { lat: 29.7958, lng: -90.8229, name: 'Thibodaux' },
  // Virginia
  'virginia beach': { lat: 36.8529, lng: -75.9780, name: 'Virginia Beach' },
  'norfolk': { lat: 36.8508, lng: -76.2859, name: 'Norfolk' },
  'chesapeake': { lat: 36.7682, lng: -76.2875, name: 'Chesapeake' },
  'richmond': { lat: 37.5407, lng: -77.4360, name: 'Richmond' },
  'newport news': { lat: 37.0871, lng: -76.4730, name: 'Newport News' },
  'hampton': { lat: 37.0299, lng: -76.3452, name: 'Hampton' },
  'alexandria': { lat: 38.8048, lng: -77.0469, name: 'Alexandria' },
  'portsmouth': { lat: 36.8354, lng: -76.2983, name: 'Portsmouth' },
  'roanoke': { lat: 37.2710, lng: -79.9414, name: 'Roanoke' },
  'lynchburg': { lat: 37.4138, lng: -79.1422, name: 'Lynchburg' },
  'fredericksburg': { lat: 38.3032, lng: -77.4605, name: 'Fredericksburg' },
  'suffolk': { lat: 36.7282, lng: -76.5836, name: 'Suffolk' },
  'woodbridge': { lat: 38.6582, lng: -77.2497, name: 'Woodbridge' },
  'manassas': { lat: 38.7509, lng: -77.4753, name: 'Manassas' },
  'sterling': { lat: 39.0062, lng: -77.4286, name: 'Sterling' },
  'dale city': { lat: 38.6370, lng: -77.3111, name: 'Dale City' },
  'centreville': { lat: 38.8401, lng: -77.4289, name: 'Centreville' },
  'williamsburg': { lat: 37.2707, lng: -76.7075, name: 'Williamsburg' },
  'fairfax': { lat: 38.8462, lng: -77.3064, name: 'Fairfax' },
  'leesburg': { lat: 39.1157, lng: -77.5636, name: 'Leesburg' },
  'charlottesville': { lat: 38.0293, lng: -78.4767, name: 'Charlottesville' },
  'danville': { lat: 36.5860, lng: -79.3950, name: 'Danville' },
  // Tennessee
  'nashville': { lat: 36.1627, lng: -86.7816, name: 'Nashville' },
  'memphis': { lat: 35.1495, lng: -90.0490, name: 'Memphis' },
  'knoxville': { lat: 35.9606, lng: -83.9207, name: 'Knoxville' },
  'chattanooga': { lat: 35.0456, lng: -85.3097, name: 'Chattanooga' },
  'clarksville': { lat: 36.5298, lng: -87.3595, name: 'Clarksville' },
  'murfreesboro': { lat: 35.8456, lng: -86.3903, name: 'Murfreesboro' },
  'franklin': { lat: 35.9251, lng: -86.8689, name: 'Franklin' },
  'jackson': { lat: 35.6145, lng: -88.8139, name: 'Jackson' },
  'johnson city': { lat: 36.3134, lng: -82.3535, name: 'Johnson City' },
  'bartlett': { lat: 35.2046, lng: -89.8740, name: 'Bartlett' },
  'hendersonville': { lat: 36.3048, lng: -86.6200, name: 'Hendersonville' },
  'kingsport': { lat: 36.5484, lng: -82.5618, name: 'Kingsport' },
  'smyrna': { lat: 35.9828, lng: -86.5186, name: 'Smyrna' },
  'gallatin': { lat: 36.3887, lng: -86.4467, name: 'Gallatin' },
  'columbia': { lat: 35.6151, lng: -87.0353, name: 'Columbia' },
  'spring hill': { lat: 35.7512, lng: -86.9300, name: 'Spring Hill' },
  'cookeville': { lat: 36.1628, lng: -85.5016, name: 'Cookeville' },
  // North Carolina
  'charlotte': { lat: 35.2271, lng: -80.8431, name: 'Charlotte' },
  'raleigh': { lat: 35.7796, lng: -78.6382, name: 'Raleigh' },
  'greensboro': { lat: 36.0726, lng: -79.7920, name: 'Greensboro' },
  'durham': { lat: 35.9940, lng: -78.8986, name: 'Durham' },
  'winston-salem': { lat: 36.0999, lng: -80.2442, name: 'Winston-Salem' },
  'fayetteville': { lat: 35.0527, lng: -78.8784, name: 'Fayetteville' },
  'cary': { lat: 35.7915, lng: -78.7811, name: 'Cary' },
  'wilmington': { lat: 34.2257, lng: -77.9447, name: 'Wilmington' },
  'high point': { lat: 35.9557, lng: -80.0053, name: 'High Point' },
  'concord': { lat: 35.4088, lng: -80.5795, name: 'Concord' },
  'asheville': { lat: 35.5951, lng: -82.5515, name: 'Asheville' },
  'gastonia': { lat: 35.2621, lng: -81.1873, name: 'Gastonia' },
  'jacksonville': { lat: 34.7540, lng: -77.4302, name: 'Jacksonville' },
  'chapel hill': { lat: 35.9132, lng: -79.0558, name: 'Chapel Hill' },
  'huntersville': { lat: 35.4107, lng: -80.8429, name: 'Huntersville' },
  'apex': { lat: 35.7327, lng: -78.8503, name: 'Apex' },
  'burlington': { lat: 36.0957, lng: -79.4378, name: 'Burlington' },
  // Alabama
  'birmingham': { lat: 33.5207, lng: -86.8025, name: 'Birmingham' },
  'montgomery': { lat: 32.3792, lng: -86.3077, name: 'Montgomery' },
  'huntsville': { lat: 34.7304, lng: -86.5861, name: 'Huntsville' },
  'mobile': { lat: 30.6954, lng: -88.0399, name: 'Mobile' },
  'tuscaloosa': { lat: 33.2098, lng: -87.5692, name: 'Tuscaloosa' },
  'hoover': { lat: 33.4054, lng: -86.8114, name: 'Hoover' },
  'dothan': { lat: 31.2232, lng: -85.3905, name: 'Dothan' },
  'auburn': { lat: 32.6099, lng: -85.4808, name: 'Auburn' },
  'decatur': { lat: 34.6059, lng: -86.9833, name: 'Decatur' },
  'madison': { lat: 34.6993, lng: -86.7483, name: 'Madison' },
  'florence': { lat: 34.7998, lng: -87.6773, name: 'Florence' },
  'gadsden': { lat: 34.0143, lng: -86.0066, name: 'Gadsden' },
  // Nevada
  'las vegas': { lat: 36.1699, lng: -115.1398, name: 'Las Vegas' },
  'henderson': { lat: 36.0395, lng: -114.9817, name: 'Henderson' },
  'reno': { lat: 39.5296, lng: -119.8138, name: 'Reno' },
  'north las vegas': { lat: 36.1989, lng: -115.1175, name: 'North Las Vegas' },
  'sparks': { lat: 39.5349, lng: -119.7527, name: 'Sparks' },
  'carson city': { lat: 39.1638, lng: -119.7674, name: 'Carson City' },
  // New Jersey
  'newark': { lat: 40.7357, lng: -74.1724, name: 'Newark' },
  'jersey city': { lat: 40.7282, lng: -74.0776, name: 'Jersey City' },
  'paterson': { lat: 40.9168, lng: -74.1718, name: 'Paterson' },
  'elizabeth': { lat: 40.6640, lng: -74.2107, name: 'Elizabeth' },
  'trenton': { lat: 40.2171, lng: -74.7429, name: 'Trenton' },
  'camden': { lat: 39.9259, lng: -75.1196, name: 'Camden' },
  'clifton': { lat: 40.8584, lng: -74.1638, name: 'Clifton' },
  'passaic': { lat: 40.8568, lng: -74.1285, name: 'Passaic' },
  'union city': { lat: 40.7795, lng: -74.0246, name: 'Union City' },
  'east orange': { lat: 40.7673, lng: -74.2049, name: 'East Orange' },
  'vineland': { lat: 39.4864, lng: -75.0260, name: 'Vineland' },
  'atlantic city': { lat: 39.3643, lng: -74.4229, name: 'Atlantic City' },
  // Mississippi
  'jackson': { lat: 32.2988, lng: -90.1848, name: 'Jackson' },
  'gulfport': { lat: 30.3674, lng: -89.0928, name: 'Gulfport' },
  'southaven': { lat: 34.9890, lng: -90.0129, name: 'Southaven' },
  'hattiesburg': { lat: 31.3271, lng: -89.2903, name: 'Hattiesburg' },
  'biloxi': { lat: 30.3960, lng: -88.8853, name: 'Biloxi' },
  'meridian': { lat: 32.3643, lng: -88.7037, name: 'Meridian' },
  'tupelo': { lat: 34.2576, lng: -88.7034, name: 'Tupelo' },
  'olive branch': { lat: 34.9618, lng: -89.8295, name: 'Olive Branch' },
  'greenville': { lat: 33.4101, lng: -91.0618, name: 'Greenville' },
  'horn lake': { lat: 34.9554, lng: -90.0548, name: 'Horn Lake' },
  // Indiana
  'indianapolis': { lat: 39.7684, lng: -86.1581, name: 'Indianapolis' },
  'fort wayne': { lat: 41.0793, lng: -85.1394, name: 'Fort Wayne' },
  'evansville': { lat: 37.9716, lng: -87.5711, name: 'Evansville' },
  'south bend': { lat: 41.6764, lng: -86.2520, name: 'South Bend' },
  'carmel': { lat: 39.9784, lng: -86.1181, name: 'Carmel' },
  'fishers': { lat: 39.9568, lng: -86.0131, name: 'Fishers' },
  'bloomington': { lat: 39.1653, lng: -86.5264, name: 'Bloomington' },
  'gary': { lat: 41.5934, lng: -87.3464, name: 'Gary' },
  'muncie': { lat: 40.1934, lng: -85.3864, name: 'Muncie' },
  'noblesville': { lat: 40.0456, lng: -86.0086, name: 'Noblesville' },
  // Kentucky
  'louisville': { lat: 38.2527, lng: -85.7585, name: 'Louisville' },
  'lexington': { lat: 38.0406, lng: -84.5037, name: 'Lexington' },
  'bowling green': { lat: 36.9685, lng: -86.4808, name: 'Bowling Green' },
  'owensboro': { lat: 37.7719, lng: -87.1112, name: 'Owensboro' },
  'covington': { lat: 39.0837, lng: -84.5086, name: 'Covington' },
  'frankfort': { lat: 38.2009, lng: -84.8733, name: 'Frankfort' },
  'paducah': { lat: 37.0834, lng: -88.6001, name: 'Paducah' },
  // Arkansas
  'little rock': { lat: 34.7465, lng: -92.2896, name: 'Little Rock' },
  'fort smith': { lat: 35.3859, lng: -94.3985, name: 'Fort Smith' },
  'fayetteville': { lat: 36.0822, lng: -94.1719, name: 'Fayetteville' },
  'springdale': { lat: 36.1867, lng: -94.1288, name: 'Springdale' },
  'jonesboro': { lat: 35.8423, lng: -90.7043, name: 'Jonesboro' },
  'rogers': { lat: 36.3320, lng: -94.1185, name: 'Rogers' },
  'conway': { lat: 35.0887, lng: -92.4421, name: 'Conway' },
  'north little rock': { lat: 34.7695, lng: -92.2671, name: 'North Little Rock' },
  'hot springs': { lat: 34.5037, lng: -93.0552, name: 'Hot Springs' },
  'pine bluff': { lat: 34.2284, lng: -92.0032, name: 'Pine Bluff' },
  // Arizona
  'phoenix': { lat: 33.4484, lng: -112.0740, name: 'Phoenix' },
  'tucson': { lat: 32.2226, lng: -110.9747, name: 'Tucson' },
  'mesa': { lat: 33.4152, lng: -111.8315, name: 'Mesa' },
  'chandler': { lat: 33.3062, lng: -111.8413, name: 'Chandler' },
  'scottsdale': { lat: 33.4942, lng: -111.9261, name: 'Scottsdale' },
  'glendale': { lat: 33.5387, lng: -112.1860, name: 'Glendale' },
  'gilbert': { lat: 33.3528, lng: -111.7890, name: 'Gilbert' },
  'tempe': { lat: 33.4255, lng: -111.9400, name: 'Tempe' },
  'peoria': { lat: 33.5806, lng: -112.2374, name: 'Peoria' },
  'surprise': { lat: 33.6292, lng: -112.3680, name: 'Surprise' },
  'flagstaff': { lat: 35.1983, lng: -111.6513, name: 'Flagstaff' },
  // Florida
  'miami': { lat: 25.7617, lng: -80.1918, name: 'Miami' },
  'orlando': { lat: 28.5383, lng: -81.3792, name: 'Orlando' },
  'tampa': { lat: 27.9506, lng: -82.4572, name: 'Tampa' },
  'jacksonville': { lat: 30.3322, lng: -81.6557, name: 'Jacksonville' },
  'st. petersburg': { lat: 27.7676, lng: -82.6403, name: 'St. Petersburg' },
  'tallahassee': { lat: 30.4383, lng: -84.2807, name: 'Tallahassee' },
  'fort lauderdale': { lat: 26.1224, lng: -80.1373, name: 'Fort Lauderdale' },
  'hialeah': { lat: 25.8576, lng: -80.2781, name: 'Hialeah' },
  'cape coral': { lat: 26.5629, lng: -81.9495, name: 'Cape Coral' },
  'pembroke pines': { lat: 26.0128, lng: -80.2241, name: 'Pembroke Pines' },
  'gainesville': { lat: 29.6516, lng: -82.3248, name: 'Gainesville' },
  'pensacola': { lat: 30.4213, lng: -87.2169, name: 'Pensacola' },
  // Georgia
  'atlanta': { lat: 33.7490, lng: -84.3880, name: 'Atlanta' },
  'savannah': { lat: 32.0809, lng: -81.0912, name: 'Savannah' },
  'augusta': { lat: 33.4735, lng: -81.9748, name: 'Augusta' },
  'columbus': { lat: 32.4610, lng: -84.9877, name: 'Columbus' },
  'macon': { lat: 32.8407, lng: -83.6324, name: 'Macon' },
  'athens': { lat: 33.9519, lng: -83.3576, name: 'Athens' },
  'marietta': { lat: 33.9526, lng: -84.5499, name: 'Marietta' },
  'roswell': { lat: 34.0232, lng: -84.3616, name: 'Roswell' },
  // Hawaii
  'honolulu': { lat: 21.3069, lng: -157.8583, name: 'Honolulu' },
  'pearl city': { lat: 21.3972, lng: -157.9750, name: 'Pearl City' },
  'hilo': { lat: 19.7297, lng: -155.0900, name: 'Hilo' },
  'kailua': { lat: 21.4022, lng: -157.7394, name: 'Kailua' },
  'waipahu': { lat: 21.3867, lng: -158.0092, name: 'Waipahu' },
  'aiea': { lat: 21.3822, lng: -157.9331, name: 'Aiea' },
  'kaneohe': { lat: 21.4180, lng: -157.8036, name: 'Kaneohe' },
  'kapolei': { lat: 21.3350, lng: -158.0580, name: 'Kapolei' },
  'ewa beach': { lat: 21.3156, lng: -158.0072, name: 'Ewa Beach' },
  // West Virginia  
  'charleston': { lat: 38.3498, lng: -81.6326, name: 'Charleston' },
  'huntington': { lat: 38.4192, lng: -82.4452, name: 'Huntington' },
  'morgantown': { lat: 39.6295, lng: -79.9559, name: 'Morgantown' },
  'parkersburg': { lat: 39.2667, lng: -81.5615, name: 'Parkersburg' },
  'wheeling': { lat: 40.0640, lng: -80.7209, name: 'Wheeling' },
  'martinsburg': { lat: 39.4562, lng: -77.9639, name: 'Martinsburg' },
  // Ohio
  'columbus': { lat: 39.9612, lng: -82.9988, name: 'Columbus' },
  'cleveland': { lat: 41.4993, lng: -81.6944, name: 'Cleveland' },
  'cincinnati': { lat: 39.1031, lng: -84.5120, name: 'Cincinnati' },
  'toledo': { lat: 41.6528, lng: -83.5379, name: 'Toledo' },
  'akron': { lat: 41.0814, lng: -81.5190, name: 'Akron' },
  'dayton': { lat: 39.7589, lng: -84.1916, name: 'Dayton' },
  // Nebraska
  'omaha': { lat: 41.2565, lng: -95.9345, name: 'Omaha' },
  'lincoln': { lat: 40.8136, lng: -96.7026, name: 'Lincoln' },
  'bellevue': { lat: 41.1544, lng: -95.8908, name: 'Bellevue' },
  'grand island': { lat: 40.9264, lng: -98.3420, name: 'Grand Island' },
  // Maryland
  'baltimore': { lat: 39.2904, lng: -76.6122, name: 'Baltimore' },
  'columbia': { lat: 39.2037, lng: -76.8610, name: 'Columbia' },
  'silver spring': { lat: 38.9907, lng: -77.0261, name: 'Silver Spring' },
  'waldorf': { lat: 38.6246, lng: -76.9392, name: 'Waldorf' },
  'glen burnie': { lat: 39.1626, lng: -76.6247, name: 'Glen Burnie' },
  'ellicott city': { lat: 39.2673, lng: -76.7983, name: 'Ellicott City' },
  'annapolis': { lat: 38.9784, lng: -76.4922, name: 'Annapolis' },
  'frederick': { lat: 39.4143, lng: -77.4105, name: 'Frederick' },
  // Wisconsin
  'milwaukee': { lat: 43.0389, lng: -87.9065, name: 'Milwaukee' },
  'green bay': { lat: 44.5133, lng: -88.0133, name: 'Green Bay' },
  'kenosha': { lat: 42.5847, lng: -87.8212, name: 'Kenosha' },
  'racine': { lat: 42.7261, lng: -87.7829, name: 'Racine' },
  'appleton': { lat: 44.2619, lng: -88.4154, name: 'Appleton' },
  // District of Columbia
  'washington': { lat: 38.9072, lng: -77.0369, name: 'Washington' },
};

// Parse city from mailing address
const parseCityFromAddress = (address) => {
  if (!address) return null;
  // Format from Salesforce: "City, ST, ZIP" (e.g., "Dallas, TX, 75201")
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    // City is always the first part
    const cityPart = parts[0];
    // Skip if it looks like a street address (contains numbers at start)
    if (/^\d/.test(cityPart)) return parts.length >= 3 ? parts[1].toLowerCase().trim() : null;
    return cityPart.toLowerCase().trim();
  }
  return null;
};

// Parse state from mailing address
const parseStateFromAddress = (address) => {
  if (!address) return null;
  const parts = address.split(',').map(p => p.trim());
  const lastPart = parts[parts.length - 1]; // "TX 78232" or "Texas"
  const stateMatch = lastPart.match(/^([A-Z]{2})\s/);
  if (stateMatch) return stateMatch[1];
  return null;
};

const parseCSV = (csv) => {
  const lines = csv.trim().split('\n');
  const parseLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };
  const headers = parseLine(lines[0]).map(h => h.replace(/^["']|["']$/g, ''));
  return lines.slice(1).map(line => {
    const values = parseLine(line);
    const obj = {};
    headers.forEach((header, index) => { obj[header] = values[index] || ''; });
    return obj;
  });
};

const HiringForecast = ({ candidateData }) => {
  const [activeTab, setActiveTab] = useState('eligible');
  const [eligibilityData, setEligibilityData] = useState([]);
  const [supervisorData, setSupervisorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedState, setSelectedState] = useState('Texas');
  const [matchedCandidates, setMatchedCandidates] = useState([]);
  const [cityDensity, setCityDensity] = useState([]);
  const [stats, setStats] = useState(null);
  const [mapZoom, setMapZoom] = useState(1);
  const [mapCenter, setMapCenter] = useState([-96, 38]);
  const [hoveredCity, setHoveredCity] = useState(null);
  const [hoveredSupervisor, setHoveredSupervisor] = useState(null);

  // Fetch eligibility + supervisor data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [eligRes, supRes] = await Promise.all([
        fetch(ELIGIBILITY_URL),
        fetch(SUPERVISOR_URL),
      ]);
      
      if (eligRes.ok) {
        const csvText = await eligRes.text();
        const parsed = parseCSV(csvText);
        console.log('Eligibility data headers:', Object.keys(parsed[0] || {}));
        setEligibilityData(parsed);
      }
      
      if (supRes.ok) {
        const csvText = await supRes.text();
        const parsed = parseCSV(csvText);
        const headers = Object.keys(parsed[0] || {});
        console.log('=== SUPERVISOR DEBUG ===');
        console.log('Supervisor headers:', headers);
        console.log('Supervisor row count:', parsed.length);
        console.log('Sample supervisor row:', JSON.stringify(parsed[0], null, 2));
        console.log('Col D (index 3):', headers[3], '→', parsed.slice(0, 3).map(r => r[headers[3]]));
        console.log('Col E (index 4):', headers[4], '→', parsed.slice(0, 3).map(r => r[headers[4]]));
        console.log('Col F (index 5):', headers[5], '→', parsed.slice(0, 3).map(r => r[headers[5]]));
        console.log('Col G-K (index 6-10):', headers.slice(6, 11));
        console.log('Sample location:', parsed[0] && headers.slice(6, 11).map(h => parsed[0][h]));
        setSupervisorData(parsed);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Process eligibility and match to candidate data
  useEffect(() => {
    if (eligibilityData.length === 0 || !candidateData || candidateData.length === 0) return;

    const headers = Object.keys(eligibilityData[0]);
    console.log('=== HIRING FORECAST DEBUG ===');
    console.log('All eligibility headers:', headers);
    console.log('Eligibility row count:', eligibilityData.length);
    console.log('Candidate row count:', candidateData.length);
    console.log('Sample eligibility row:', JSON.stringify(eligibilityData[0], null, 2));
    
    const colD = headers[3]; // Column D (0-indexed)
    const colE = headers[4]; // Column E
    console.log(`Column D header: "${colD}", Column E header: "${colE}"`);
    console.log('Sample Col D values:', eligibilityData.slice(0, 5).map(r => r[colD]));
    console.log('Sample Col E values:', eligibilityData.slice(0, 5).map(r => r[colE]));

    // Find eligible candidates: Col D - Col E == 1
    const eligible = eligibilityData.filter(row => {
      const valD = parseFloat(row[colD]) || 0;
      const valE = parseFloat(row[colE]) || 0;
      return (valD - valE) === 1;
    });

    console.log(`Eligible to be hired: ${eligible.length} of ${eligibilityData.length}`);
    if (eligible.length > 0) {
      console.log('Sample eligible row:', JSON.stringify(eligible[0], null, 2));
    }

    // Find email column — try multiple approaches
    const emailCol = headers.find(h => h.toLowerCase().includes('email')) || headers[1];
    console.log(`Email column: "${emailCol}"`);
    
    const eligibleEmails = new Set(
      eligible.map(r => (r[emailCol] || '').toLowerCase().trim()).filter(Boolean)
    );
    console.log(`Unique eligible emails: ${eligibleEmails.size}`);
    console.log('Sample eligible emails:', [...eligibleEmails].slice(0, 5));

    // Check what email column exists in candidate data
    const candidateHeaders = Object.keys(candidateData[0] || {});
    console.log('Candidate data headers:', candidateHeaders);
    console.log('Sample candidate email:', candidateData[0]?.['Email']);
    
    // Match to candidate database by email — try partial match too
    const matched = candidateData.filter(r => {
      const email = (r['Email'] || '').toLowerCase().trim();
      if (!email) return false;
      // Try exact match first
      if (eligibleEmails.has(email)) return true;
      // Try matching just the part before @ in case of formatting differences
      const emailPrefix = email.split('@')[0];
      for (const eligEmail of eligibleEmails) {
        if (eligEmail.startsWith(emailPrefix + '@')) return true;
        // Also check if eligibility email is a partial (no domain)
        if (eligEmail === emailPrefix) return true;
      }
      return false;
    });

    console.log(`Matched to candidate database: ${matched.length}`);
    if (matched.length > 0) {
      console.log('Sample matched candidate:', JSON.stringify({
        email: matched[0]['Email'],
        state: matched[0]['Certification state'],
        address: matched[0]['Mailing Address'],
      }));
    } else {
      // Debug: show what emails look like in each dataset
      console.log('NO MATCHES FOUND. Comparing formats:');
      console.log('Eligibility emails (first 3):', [...eligibleEmails].slice(0, 3));
      console.log('Candidate emails (first 3):', candidateData.slice(0, 3).map(r => r['Email']));
    }

    setMatchedCandidates(matched);

    // Filter to enrolled within past 2 years
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
    
    const recentMatched = matched.filter(r => {
      if (!r['Enrolled']) return false;
      const enrollDate = new Date(r['Enrolled']);
      return enrollDate >= twoYearsAgo;
    });

    console.log(`Matched within 2-year enrollment window: ${recentMatched.length} of ${matched.length}`);
    setMatchedCandidates(recentMatched);

    // Build stats
    const stateCounts = {};
    recentMatched.forEach(r => {
      const state = r['Certification state'] || 'Unknown';
      stateCounts[state] = (stateCounts[state] || 0) + 1;
    });

    const sortedStates = Object.entries(stateCounts).sort((a, b) => b[1] - a[1]);
    
    setStats({
      totalEligible: eligible.length,
      totalMatched: recentMatched.length,
      stateBreakdown: sortedStates,
      topState: sortedStates[0] || ['N/A', 0],
    });

    // Default to top state if current selection has no data
    if (sortedStates.length > 0) {
      const currentStateHasData = sortedStates.some(([s]) => s === selectedState);
      if (!currentStateHasData) {
        setSelectedState(sortedStates[0][0]);
      }
    }

  }, [eligibilityData, candidateData]);

  // Process supervisor data — capacity analysis
  const supervisorsByState = useMemo(() => {
    if (supervisorData.length === 0) return {};
    
    const headers = Object.keys(supervisorData[0]);
    console.log('=== SUPERVISOR COLUMN MAPPING ===');
    console.log('All headers:', headers);
    // Log all values for first row to understand layout
    headers.forEach((h, i) => {
      console.log(`  Col ${String.fromCharCode(65+i)} (${i}): "${h}" = "${supervisorData[0][h]}"`);
    });
    
    // Build abbreviation lookup
    const abbrevToState = {};
    Object.entries(STATE_GEO).forEach(([name, geo]) => { abbrevToState[geo.abbr] = name; });
    const stateAbbrevs = new Set(Object.values(STATE_GEO).map(g => g.abbr));
    const stateNames = new Set(Object.keys(STATE_GEO));
    
    // Auto-detect columns by scanning values
    let colMax = null, colAssigned1 = null, colAssigned2 = null, colState = null, colCity = null, colName = null, colLastName = null;
    
    // Find state column — look for column where most values are 2-letter state abbreviations or state names
    headers.forEach((h, idx) => {
      const vals = supervisorData.slice(0, 20).map(r => (r[h] || '').trim());
      const stateMatches = vals.filter(v => stateAbbrevs.has(v.toUpperCase()) || stateNames.has(v)).length;
      if (stateMatches >= 5 && !colState) {
        colState = h;
        console.log(`  → Detected STATE column: "${h}" (index ${idx})`);
      }
    });
    
    // Find city column — usually one before state
    if (colState) {
      const stateIdx = headers.indexOf(colState);
      if (stateIdx > 0) {
        colCity = headers[stateIdx - 1];
        console.log(`  → Detected CITY column: "${colCity}" (index ${stateIdx - 1})`);
      }
    }
    
    // Col A = name (first or full), Col B = last name
    colName = headers[0];
    colLastName = headers[1];
    
    // Col D = max capacity (index 3), Col E = assigned1 (index 4), Col F = assigned2 (index 5)
    colMax = headers[3];
    colAssigned1 = headers[4];
    colAssigned2 = headers[5];
    
    console.log(`  → Name: "${colName}" + "${colLastName}"`);
    console.log(`  → Max capacity: "${colMax}" (sample: ${supervisorData[0][colMax]})`);
    console.log(`  → Assigned1: "${colAssigned1}" (sample: ${supervisorData[0][colAssigned1]})`);
    console.log(`  → Assigned2: "${colAssigned2}" (sample: ${supervisorData[0][colAssigned2]})`);
    
    const byState = {};
    let skippedNoState = 0;
    
    supervisorData.forEach(row => {
      const firstName = (row[colName] || '').trim();
      const lastName = (row[colLastName] || '').trim();
      const name = lastName ? `${firstName} ${lastName}` : firstName;
      const maxCap = parseFloat(row[colMax]) || 0;
      const assigned1 = parseFloat(row[colAssigned1]) || 0;
      const assigned2 = parseFloat(row[colAssigned2]) || 0;
      const currentLoad = assigned1 + assigned2;
      const remaining = maxCap - currentLoad;
      const rawState = colState ? (row[colState] || '').trim() : '';
      const city = colCity ? (row[colCity] || '').trim() : '';
      
      if (!name || !rawState) { skippedNoState++; return; }
      
      // Normalize state
      let stateName = rawState;
      if (rawState.length === 2) {
        stateName = abbrevToState[rawState.toUpperCase()] || rawState;
      }
      
      if (!byState[stateName]) {
        byState[stateName] = { supervisors: [], totalMax: 0, totalAssigned: 0, totalRemaining: 0 };
      }
      
      // Determine capacity status
      const utilizationPct = maxCap > 0 ? (currentLoad / maxCap) * 100 : 100;
      let capacityStatus = 'sufficient'; // green
      if (utilizationPct >= 90) capacityStatus = 'none'; // red
      else if (utilizationPct >= 70) capacityStatus = 'closing'; // yellow
      
      // Geocode supervisor
      const cityKey = city.toLowerCase();
      const geo = TEXAS_METROS[cityKey] || US_OTHER_CITIES[cityKey];
      
      byState[stateName].supervisors.push({
        name,
        maxCapacity: maxCap,
        currentLoad,
        remaining,
        capacityStatus,
        utilizationPct,
        city,
        state: stateName,
        lat: geo?.lat || null,
        lng: geo?.lng || null,
      });
      
      byState[stateName].totalMax += maxCap;
      byState[stateName].totalAssigned += currentLoad;
      byState[stateName].totalRemaining += remaining;
    });
    
    console.log(`Supervisor summary: ${supervisorData.length} rows, ${skippedNoState} skipped (no state)`);
    console.log('Supervisors by state:', Object.entries(byState).map(([s, d]) => 
      `${s}: ${d.supervisors.length} supervisors, ${d.totalMax} max, ${d.totalAssigned} assigned, ${d.totalRemaining} remaining`
    ));
    
    return byState;
  }, [supervisorData]);

  // State-level capacity risk assessment
  const stateCapacityRisk = useMemo(() => {
    const risk = {};
    Object.entries(supervisorsByState).forEach(([state, data]) => {
      const eligibleIncoming = matchedCandidates.filter(r => r['Certification state'] === state).length;
      const netCapacity = data.totalRemaining - eligibleIncoming;
      const supCount = data.supervisors.length;
      const sufficientCount = data.supervisors.filter(s => s.capacityStatus === 'sufficient').length;
      const closingCount = data.supervisors.filter(s => s.capacityStatus === 'closing').length;
      const noneCount = data.supervisors.filter(s => s.capacityStatus === 'none').length;
      
      let overallRisk = 'sufficient';
      if (netCapacity <= 0) overallRisk = 'none';
      else if (netCapacity < eligibleIncoming * 0.3) overallRisk = 'closing';
      
      risk[state] = {
        ...data,
        eligibleIncoming,
        netCapacity,
        overallRisk,
        supCount,
        sufficientCount,
        closingCount,
        noneCount,
      };
    });
    return risk;
  }, [supervisorsByState, matchedCandidates]);

  // Proximity coverage analysis — which candidates are within ~45 min drive of a supervisor with capacity?
  // ~35 miles ≈ 0.5° lat, adjusted for longitude at different latitudes
  const DRIVE_RADIUS_DEG = 0.5; // ~35 miles, proxy for 45 min drive

  const proximityCoverage = useMemo(() => {
    const stateSups = supervisorsByState[selectedState]?.supervisors || [];
    const geocodedSups = stateSups.filter(s => s.lat && s.lng);
    
    if (geocodedSups.length === 0 || cityDensity.length === 0) {
      return { covered: [], atRisk: [], uncovered: [], ungeocodedCandidates: 0 };
    }

    const covered = [];    // nearby supervisor WITH capacity
    const atRisk = [];     // nearby supervisor but CLOSING or FULL
    const uncovered = [];  // NO supervisor within radius
    let ungeocodedCandidates = 0;

    cityDensity.forEach(city => {
      if (!city.lat || !city.lng) {
        ungeocodedCandidates += city.count;
        return;
      }

      // Find all supervisors within radius
      const nearbySups = geocodedSups.filter(s => {
        const dLat = Math.abs(s.lat - city.lat);
        const dLng = Math.abs(s.lng - city.lng) * Math.cos(city.lat * Math.PI / 180); // adjust for latitude
        return Math.sqrt(dLat * dLat + dLng * dLng) <= DRIVE_RADIUS_DEG;
      });

      if (nearbySups.length === 0) {
        uncovered.push({ ...city, nearestSupDist: null, nearbySups: [] });
      } else {
        const hasCapacity = nearbySups.some(s => s.capacityStatus === 'sufficient');
        const hasClosing = nearbySups.some(s => s.capacityStatus === 'closing');
        const totalNearbyRemaining = nearbySups.reduce((sum, s) => sum + Math.max(0, s.remaining), 0);
        
        const entry = { 
          ...city, 
          nearbySups, 
          totalNearbyRemaining,
          nearbySupNames: nearbySups.map(s => `${s.name} (${s.remaining} slots)`),
        };

        if (hasCapacity) {
          covered.push(entry);
        } else if (hasClosing) {
          atRisk.push(entry);
        } else {
          // All nearby supervisors are full
          uncovered.push(entry);
        }
      }
    });

    // Sort uncovered by candidate count descending (biggest gaps first)
    uncovered.sort((a, b) => b.count - a.count);
    atRisk.sort((a, b) => b.count - a.count);

    return { 
      covered, 
      atRisk, 
      uncovered, 
      ungeocodedCandidates,
      coveredCount: covered.reduce((s, c) => s + c.count, 0),
      atRiskCount: atRisk.reduce((s, c) => s + c.count, 0),
      uncoveredCount: uncovered.reduce((s, c) => s + c.count, 0),
    };
  }, [cityDensity, supervisorsByState, selectedState]);

  // Build city density for selected state
  useEffect(() => {
    if (matchedCandidates.length === 0) return;

    const stateFiltered = matchedCandidates.filter(r => 
      r['Certification state'] === selectedState
    );

    // Group by city from mailing address
    const cityMap = {};
    stateFiltered.forEach(r => {
      const address = r['Mailing Address'] || '';
      const city = parseCityFromAddress(address);
      if (city) {
        const key = city.toLowerCase();
        if (!cityMap[key]) {
          cityMap[key] = { name: city, count: 0, candidates: [] };
        }
        cityMap[key].count++;
        cityMap[key].candidates.push(r);
      }
    });

    // Geocode all cities
    const densityList = Object.values(cityMap)
      .map(c => {
        const key = c.name.toLowerCase();
        const geo = TEXAS_METROS[key] || US_OTHER_CITIES[key];
        return {
          ...c,
          name: geo ? geo.name : c.name.charAt(0).toUpperCase() + c.name.slice(1),
          lat: geo?.lat || null,
          lng: geo?.lng || null,
        };
      })
      .sort((a, b) => b.count - a.count);

    setCityDensity(densityList);
  }, [matchedCandidates, selectedState]);

  // Cluster cities based on zoom level — merge nearby cities at low zoom
  const clusteredCities = useMemo(() => {
    const geocoded = cityDensity.filter(c => c.lat && c.lng);
    if (geocoded.length === 0) return [];

    // Distance threshold decreases as zoom increases (more granular)
    // At zoom 1: ~2.0 degrees (~130 miles) — merge metros
    // At zoom 3.5: ~0.5 degrees (~35 miles) — separate major cities
    // At zoom 6+: ~0.15 degrees (~10 miles) — individual cities
    const threshold = 2.5 / mapZoom;

    const clusters = [];
    const used = new Set();

    for (let i = 0; i < geocoded.length; i++) {
      if (used.has(i)) continue;
      
      const cluster = {
        cities: [geocoded[i]],
        count: geocoded[i].count,
        lat: geocoded[i].lat,
        lng: geocoded[i].lng,
        name: geocoded[i].name,
        topCity: geocoded[i].name,
      };
      used.add(i);

      for (let j = i + 1; j < geocoded.length; j++) {
        if (used.has(j)) continue;
        const dist = Math.sqrt(
          Math.pow(geocoded[j].lat - cluster.lat, 2) + 
          Math.pow(geocoded[j].lng - cluster.lng, 2)
        );
        if (dist < threshold) {
          cluster.cities.push(geocoded[j]);
          cluster.count += geocoded[j].count;
          // Weighted center
          const totalCount = cluster.count;
          cluster.lat = (cluster.lat * (totalCount - geocoded[j].count) + geocoded[j].lat * geocoded[j].count) / totalCount;
          cluster.lng = (cluster.lng * (totalCount - geocoded[j].count) + geocoded[j].lng * geocoded[j].count) / totalCount;
          used.add(j);
        }
      }

      // Name: if multiple cities, use the biggest + list cities
      if (cluster.cities.length > 1) {
        const sortedCities = cluster.cities.sort((a, b) => b.count - a.count);
        cluster.name = `${sortedCities[0].name} area`;
        cluster.topCity = sortedCities[0].name;
        const cityNames = sortedCities.slice(0, 6).map(c => `${c.name} (${c.count})`);
        if (sortedCities.length > 6) cityNames.push(`+${sortedCities.length - 6} more`);
        cluster.subLabel = cityNames.join(', ');
        cluster.cityCount = cluster.cities.length;
      }

      clusters.push(cluster);
    }

    return clusters.sort((a, b) => b.count - a.count);
  }, [cityDensity, mapZoom]);

  const maxClusterCount = clusteredCities.length > 0 ? clusteredCities[0].count : 1;

  // Get available states from matched candidates
  const US_STATES_SET = new Set(Object.keys(STATE_GEO));
  const availableStates = [...new Set(
    matchedCandidates.map(r => r['Certification state']).filter(s => s && US_STATES_SET.has(s))
  )].sort();

  const maxDensity = cityDensity.length > 0 ? cityDensity[0].count : 1;

  // FIPS code → state name mapping for choropleth
  const FIPS_TO_STATE = {
    '01':'Alabama','02':'Alaska','04':'Arizona','05':'Arkansas','06':'California',
    '08':'Colorado','09':'Connecticut','10':'Delaware','11':'District of Columbia',
    '12':'Florida','13':'Georgia','15':'Hawaii','16':'Idaho','17':'Illinois',
    '18':'Indiana','19':'Iowa','20':'Kansas','21':'Kentucky','22':'Louisiana',
    '23':'Maine','24':'Maryland','25':'Massachusetts','26':'Michigan','27':'Minnesota',
    '28':'Mississippi','29':'Missouri','30':'Montana','31':'Nebraska','32':'Nevada',
    '33':'New Hampshire','34':'New Jersey','35':'New Mexico','36':'New York',
    '37':'North Carolina','38':'North Dakota','39':'Ohio','40':'Oklahoma','41':'Oregon',
    '42':'Pennsylvania','44':'Rhode Island','45':'South Carolina','46':'South Dakota',
    '47':'Tennessee','48':'Texas','49':'Utah','50':'Vermont','51':'Virginia',
    '53':'Washington','54':'West Virginia','55':'Wisconsin','56':'Wyoming'
  };

  // State-level candidate counts for choropleth coloring
  const stateCandidateCounts = useMemo(() => {
    const counts = {};
    matchedCandidates.forEach(r => {
      const state = r['Certification state'];
      if (state) counts[state] = (counts[state] || 0) + 1;
    });
    return counts;
  }, [matchedCandidates]);

  const maxStateCount = Math.max(...Object.values(stateCandidateCounts), 1);

  // Color scale for choropleth
  const getStateColor = (count) => {
    if (!count || count === 0) return 'rgba(255, 255, 255, 0.03)';
    const intensity = Math.pow(count / maxStateCount, 0.5); // sqrt scale for better distribution
    const r = Math.round(0 + intensity * 0);
    const g = Math.round(50 + intensity * 140);
    const b = Math.round(80 + intensity * 88);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Handle state click — zoom in + select
  const handleStateClick = (stateName, count) => {
    if (!stateName || count <= 0) return;
    setSelectedState(stateName);
    const geo = STATE_GEO[stateName];
    if (geo) {
      // Zoom levels vary by state size
      const zoomLevels = {
        'Texas': 3.5, 'Alaska': 2, 'California': 4, 'Montana': 4,
        'New Mexico': 4.5, 'Arizona': 4.5, 'Nevada': 4.5, 'Colorado': 5,
        'Oregon': 4.5, 'Wyoming': 5, 'Michigan': 5, 'Utah': 5,
        'Minnesota': 4.5, 'Florida': 5, 'Georgia': 5.5, 'Virginia': 6,
        'Tennessee': 5.5, 'North Carolina': 5, 'Louisiana': 6,
        'Alabama': 6, 'Mississippi': 6, 'Arkansas': 6, 'Kentucky': 6,
        'Indiana': 6, 'Ohio': 6, 'West Virginia': 7, 'Maryland': 8,
        'New Jersey': 8, 'Hawaii': 6, 'Nebraska': 5, 'Wisconsin': 5,
        'District of Columbia': 12,
      };
      setMapZoom(zoomLevels[stateName] || 5);
      setMapCenter([geo.lng, geo.lat]);
    }
  };

  // Zoom out to national view
  const handleZoomOut = () => {
    setMapZoom(1);
    setMapCenter([-96, 38]);
  };


  // Debug info
  const debugInfo = {
    eligibilityRows: eligibilityData.length,
    eligibleCount: stats?.totalEligible || 0,
    matchedCount: stats?.totalMatched || 0,
    candidateRows: candidateData?.length || 0,
    statesFound: availableStates.length,
    selectedState,
    citiesForState: cityDensity.length,
    geocodedCities: cityDensity.filter(c => c.lat && c.lng).length,
  };

  return (
    <div className="hiring-forecast">
      {/* Map tabs */}
      <div className="hf-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'eligible'}
          className={`hf-tab ${activeTab === 'eligible' ? 'active' : ''}`}
          onClick={() => setActiveTab('eligible')}
        >
          <Users size={15} />
          Eligible to Hire
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'placements'}
          className={`hf-tab ${activeTab === 'placements' ? 'active' : ''}`}
          onClick={() => setActiveTab('placements')}
        >
          <MapPin size={15} />
          Placements &amp; Supervisors
        </button>
      </div>

      {activeTab === 'placements' ? <PlacementMap semester="Fall 2026" /> : loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px', color: '#c7d2fe' }}>
          <RefreshCw size={24} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '0.75rem' }}>Loading hiring forecast data...</span>
        </div>
      ) : (
      <>
      {/* KPI Row */}
      <div className="hf-kpi-row">
        <div className="hf-kpi">
          <div className="hf-kpi-value">{stats?.totalEligible?.toLocaleString() || '—'}</div>
          <div className="hf-kpi-label">Eligible to Hire</div>
          <div className="hf-kpi-sub">All states · D - E = 1</div>
        </div>
        <div className="hf-kpi">
          <div className="hf-kpi-value">{stats?.totalMatched?.toLocaleString() || '—'}</div>
          <div className="hf-kpi-label">Matched in Pipeline</div>
          <div className="hf-kpi-sub">All states · Enrolled past 2 yrs</div>
        </div>
        <div className="hf-kpi" style={{ border: '1px solid rgba(0, 190, 168, 0.3)' }}>
          <div className="hf-kpi-value" style={{ color: '#4ADE80' }}>
            {(stateCandidateCounts[selectedState] || 0).toLocaleString()}
          </div>
          <div className="hf-kpi-label">{selectedState}</div>
          <div className="hf-kpi-sub">
            {stats?.totalMatched > 0 
              ? `${((stateCandidateCounts[selectedState] || 0) / stats.totalMatched * 100).toFixed(1)}% of matched pipeline`
              : '—'
            }
          </div>
        </div>
        <div className="hf-kpi">
          <div className="hf-kpi-value">{availableStates.length}</div>
          <div className="hf-kpi-label">States Represented</div>
          <div className="hf-kpi-sub">Top: {stats?.topState?.[0] || '—'} ({stats?.topState?.[1]?.toLocaleString() || 0})</div>
        </div>
      </div>

      {/* State Selector + Density Map */}
      <div className="hf-map-card">
        <div className="hf-map-header">
          <h3 className="hf-section-title">
            <MapPin size={18} />
            Geographic Density — Eligible to Hire
          </h3>
          <div className="hf-state-selector">
            <Filter size={14} />
            <select 
              value={selectedState}
              onChange={(e) => handleStateClick(e.target.value, stateCandidateCounts[e.target.value] || 1)}
              className="hf-state-dropdown"
            >
              {availableStates.map(s => (
                <option key={s} value={s}>{s} ({matchedCandidates.filter(r => r['Certification state'] === s).length})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Choropleth Map with City Density Markers */}
        <div className="hf-map-container" style={{ position: 'relative' }}>
          {mapZoom > 1 && (
            <button 
              onClick={handleZoomOut}
              className="hf-zoom-out-btn"
            >
              ← All States
            </button>
          )}
          <ComposableMap
            projection="geoAlbersUsa"
            projectionConfig={{ scale: 900 }}
            width={800}
            height={520}
            style={{ width: '100%', height: 'auto', maxHeight: '700px', background: 'rgba(15, 23, 42, 0.6)', borderRadius: '8px' }}
          >
            <ZoomableGroup
              center={mapCenter}
              zoom={mapZoom}
              minZoom={1}
              maxZoom={12}
              onMoveEnd={({ coordinates, zoom }) => {
                setMapCenter(coordinates);
                setMapZoom(zoom);
              }}
            >
              <Geographies geography={US_TOPO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const fips = geo.id;
                    const stateName = FIPS_TO_STATE[fips];
                    const count = stateCandidateCounts[stateName] || 0;
                    const isSelected = stateName === selectedState;
                    return (
                      <Geography
                        key={geo.rpiKey || fips}
                        geography={geo}
                        onClick={() => handleStateClick(stateName, count)}
                        onMouseEnter={(e) => {
                          if (stateName && count > 0 && stateName !== selectedState) {
                            const rect = e.target.closest('svg').getBoundingClientRect();
                            setHoveredCity({
                              name: stateName,
                              count: count,
                              isState: true,
                              screenX: e.clientX - rect.left,
                              screenY: e.clientY - rect.top,
                            });
                          }
                        }}
                        onMouseMove={(e) => {
                          if (hoveredCity?.isState && hoveredCity?.name === stateName) {
                            const rect = e.target.closest('svg').getBoundingClientRect();
                            setHoveredCity(prev => prev ? ({
                              ...prev,
                              screenX: e.clientX - rect.left,
                              screenY: e.clientY - rect.top,
                            }) : null);
                          }
                        }}
                        onMouseLeave={() => {
                          if (hoveredCity?.isState) setHoveredCity(null);
                        }}
                        style={{
                          default: {
                            fill: isSelected ? 'rgba(0, 190, 168, 0.25)' : getStateColor(count),
                            stroke: isSelected ? '#4ADE80' : 'rgba(199, 210, 254, 0.15)',
                            strokeWidth: isSelected ? 1.5 : 0.5,
                            cursor: count > 0 ? 'pointer' : 'default',
                            outline: 'none',
                          },
                          hover: {
                            fill: isSelected ? 'rgba(0, 190, 168, 0.25)' : getStateColor(count),
                            stroke: count > 0 ? '#00BEA8' : 'rgba(199, 210, 254, 0.15)',
                            strokeWidth: count > 0 ? 2 : 0.5,
                            outline: 'none',
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
              {/* Candidate cluster markers — always BLUE */}
              {clusteredCities.map((city, i) => {
                const intensity = city.count / maxClusterCount;
                const baseRadius = Math.max(8, Math.min(30, 8 + intensity * 22));
                const radius = baseRadius / Math.pow(mapZoom, 0.3);
                const fontSize = Math.max(4, 8 / Math.pow(mapZoom, 0.3));
                const isHovered = hoveredCity && !hoveredCity.isState && hoveredCity.name === city.name;

                // Coverage status still tracked for tooltip
                const hasSupervisorData = (supervisorsByState[selectedState]?.supervisors || []).filter(s => s.lat && s.lng).length > 0;
                let coverageStatus = 'covered';
                if (hasSupervisorData && city.lat && city.lng) {
                  const geocodedSups = (supervisorsByState[selectedState]?.supervisors || []).filter(s => s.lat && s.lng);
                  const nearbySups = geocodedSups.filter(s => {
                    const dLat = Math.abs(s.lat - city.lat);
                    const dLng = Math.abs(s.lng - city.lng) * Math.cos(city.lat * Math.PI / 180);
                    return Math.sqrt(dLat * dLat + dLng * dLng) <= DRIVE_RADIUS_DEG;
                  });
                  if (nearbySups.length === 0) {
                    coverageStatus = 'uncovered';
                  } else if (!nearbySups.some(s => s.capacityStatus === 'sufficient')) {
                    coverageStatus = nearbySups.some(s => s.capacityStatus === 'closing') ? 'at-risk' : 'uncovered';
                  }
                }

                return (
                  <Marker key={`${i}-${city.name}`} coordinates={[city.lng, city.lat]}>
                    <g
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                        const rect = e.target.closest('svg').getBoundingClientRect();
                        setHoveredCity({ ...city, isState: false, coverageStatus, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top });
                      }}
                      onMouseMove={(e) => {
                        if (hoveredCity && !hoveredCity.isState) {
                          const rect = e.target.closest('svg').getBoundingClientRect();
                          setHoveredCity(prev => prev ? ({ ...prev, screenX: e.clientX - rect.left, screenY: e.clientY - rect.top }) : null);
                        }
                      }}
                      onMouseLeave={() => setHoveredCity(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Glow */}
                      <circle r={radius * 1.5} fill={`rgba(96, 165, 250, ${0.08 + intensity * 0.1})`} />
                      {/* Main bubble — always blue */}
                      <circle r={radius}
                        fill={isHovered ? 'rgba(96, 165, 250, 0.9)' : `rgba(96, 165, 250, ${0.4 + intensity * 0.4})`}
                        stroke={isHovered ? '#fff' : 'rgba(96, 165, 250, 0.8)'}
                        strokeWidth={isHovered ? 0.8 / Math.pow(mapZoom, 0.3) : 0.4 / Math.pow(mapZoom, 0.3)}
                      />
                      {/* Count label */}
                      <text textAnchor="middle" y={fontSize * 0.35} fill="white" fontSize={fontSize} fontWeight="700" style={{ pointerEvents: 'none' }}>
                        {city.count}
                      </text>
                      {/* City name */}
                      {mapZoom >= 2.5 && radius > 3 && (
                        <text textAnchor="middle" y={radius + fontSize + 1} fill="rgba(199, 210, 254, 0.9)" fontSize={fontSize * 0.8} fontWeight="500" style={{ pointerEvents: 'none' }}>
                          {city.name}
                        </text>
                      )}
                    </g>
                  </Marker>
                );
              })}
              {/* Supervisor capacity markers — green/yellow/red pins */}
              {(supervisorsByState[selectedState]?.supervisors || [])
                .filter(s => s.lat && s.lng)
                .map((sup, i) => {
                  const pinSize = 3.5 / Math.pow(mapZoom, 0.3);
                  const colors = { sufficient: '#4ADE80', closing: '#FBBF24', none: '#EF4444' };
                  const color = colors[sup.capacityStatus];
                  return (
                    <Marker key={`sup-${i}`} coordinates={[sup.lng, sup.lat]}>
                      <g
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          const rect = e.target.closest('svg').getBoundingClientRect();
                          setHoveredSupervisor({
                            ...sup,
                            screenX: e.clientX - rect.left,
                            screenY: e.clientY - rect.top,
                          });
                        }}
                        onMouseMove={(e) => {
                          if (hoveredSupervisor) {
                            const rect = e.target.closest('svg').getBoundingClientRect();
                            setHoveredSupervisor(prev => prev ? ({
                              ...prev,
                              screenX: e.clientX - rect.left,
                              screenY: e.clientY - rect.top,
                            }) : null);
                          }
                        }}
                        onMouseLeave={() => setHoveredSupervisor(null)}
                        style={{ cursor: 'pointer' }}
                      >
                        {/* Small solid pin for supervisors */}
                        <circle r={pinSize} fill={color} stroke="#1e1b4b" strokeWidth={0.4 / Math.pow(mapZoom, 0.3)} />
                      </g>
                    </Marker>
                  );
                })
              }
            </ZoomableGroup>
          </ComposableMap>

          {/* Hover tooltip */}
          {hoveredCity && (
            <div className="hf-map-tooltip" style={{
              left: Math.min(hoveredCity.screenX + 15, 500),
              top: hoveredCity.screenY - 50,
              maxWidth: hoveredCity.subLabel ? '280px' : '200px',
            }}>
              <div className="hf-tooltip-city">{hoveredCity.name}</div>
              <div className="hf-tooltip-count">
                {hoveredCity.count} eligible candidate{hoveredCity.count !== 1 ? 's' : ''}
                {hoveredCity.cityCount && ` · ${hoveredCity.cityCount} cities`}
              </div>
              {hoveredCity.coverageStatus && hoveredCity.coverageStatus !== 'covered' && (
                <div className="hf-tooltip-sub" style={{ color: hoveredCity.coverageStatus === 'uncovered' ? '#EF4444' : '#FBBF24', fontWeight: 600 }}>
                  {hoveredCity.coverageStatus === 'uncovered' ? '⚠ No supervisor within 45 min' : '⚠ Nearby supervisors near capacity'}
                </div>
              )}
              {hoveredCity.coverageStatus === 'covered' && (
                <div className="hf-tooltip-sub" style={{ color: '#4ADE80' }}>✓ Supervisor coverage available</div>
              )}
              {hoveredCity.isState && <div className="hf-tooltip-action">Click to zoom in</div>}
              {hoveredCity.subLabel && !hoveredCity.isState && (
                <div className="hf-tooltip-sub">{hoveredCity.subLabel}</div>
              )}
            </div>
          )}

          {/* Supervisor hover tooltip */}
          {hoveredSupervisor && (
            <div className="hf-map-tooltip" style={{
              left: Math.min(hoveredSupervisor.screenX + 15, 500),
              top: hoveredSupervisor.screenY - 60,
              maxWidth: '250px',
              borderColor: hoveredSupervisor.capacityStatus === 'sufficient' ? '#4ADE80' : hoveredSupervisor.capacityStatus === 'closing' ? '#FBBF24' : '#EF4444',
            }}>
              <div className="hf-tooltip-city">{hoveredSupervisor.name}</div>
              <div className="hf-tooltip-count" style={{ color: hoveredSupervisor.capacityStatus === 'sufficient' ? '#4ADE80' : hoveredSupervisor.capacityStatus === 'closing' ? '#FBBF24' : '#EF4444' }}>
                {hoveredSupervisor.currentLoad} / {hoveredSupervisor.maxCapacity} assigned ({Math.round(hoveredSupervisor.utilizationPct)}%)
              </div>
              <div className="hf-tooltip-sub">
                {hoveredSupervisor.remaining > 0 ? `${hoveredSupervisor.remaining} slots remaining` : 'At capacity'}
                {hoveredSupervisor.city && ` · ${hoveredSupervisor.city}`}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span>{mapZoom > 1 ? `Viewing ${selectedState} · Scroll to zoom · Drag to pan` : 'Click a state to zoom in'}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: 'rgba(96, 165, 250, 0.6)', border: '1.5px solid #60A5FA' }}></div>
                <span>Eligible Candidates</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4ADE80', border: '1px solid #1e1b4b' }}></div>
                <span>Supervisor (capacity)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FBBF24', border: '1px solid #1e1b4b' }}></div>
                <span>Supervisor (closing)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EF4444', border: '1px solid #1e1b4b' }}></div>
                <span>Supervisor (at capacity)</span>
              </div>
            </div>
          </div>
        </div>

        {/* City Table */}
        <div className="hf-city-table">
          <div className="hf-city-table-header">
            <span>City</span>
            <span>Eligible Candidates</span>
            <span>Density</span>
          </div>
          {cityDensity.slice(0, 20).map((city, i) => {
            const pct = maxDensity > 0 ? (city.count / maxDensity * 100) : 0;
            return (
              <div key={i} className="hf-city-row">
                <span className="hf-city-name">
                  {city.lat ? <MapPin size={12} style={{ color: '#00BEA8', marginRight: '0.35rem' }} /> : null}
                  {city.name}
                </span>
                <span className="hf-city-count">{city.count}</span>
                <span className="hf-city-bar-cell">
                  <div className="hf-city-bar" style={{ width: `${pct}%` }}></div>
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Coverage Analysis — 45 min drive radius */}
      {Object.keys(supervisorsByState).length > 0 && (
        <div className="hf-map-card">
          <h3 className="hf-section-title" style={{ marginBottom: '1rem' }}>
            <MapPin size={18} />
            Coverage Analysis — {selectedState}
            <span style={{ fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 400, marginLeft: '0.5rem' }}>~45 min drive radius</span>
          </h3>
          
          {stateCapacityRisk[selectedState] ? (() => {
            const risk = stateCapacityRisk[selectedState];
            const riskColors = { sufficient: '#4ADE80', closing: '#FBBF24', none: '#EF4444' };
            const totalCandidates = proximityCoverage.coveredCount + proximityCoverage.atRiskCount + proximityCoverage.uncoveredCount;
            const coveredPct = totalCandidates > 0 ? (proximityCoverage.coveredCount / totalCandidates * 100) : 0;
            const atRiskPct = totalCandidates > 0 ? (proximityCoverage.atRiskCount / totalCandidates * 100) : 0;
            const uncoveredPct = totalCandidates > 0 ? (proximityCoverage.uncoveredCount / totalCandidates * 100) : 0;
            
            return (
              <>
                {/* Coverage KPIs */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
                  <div className="hf-kpi" style={{ padding: '0.75rem', borderLeft: '3px solid #4ADE80' }}>
                    <div className="hf-kpi-value" style={{ fontSize: '1.5rem', color: '#4ADE80' }}>{proximityCoverage.coveredCount}</div>
                    <div className="hf-kpi-label" style={{ fontSize: '0.75rem' }}>Covered</div>
                    <div className="hf-kpi-sub">Supervisor nearby with capacity</div>
                  </div>
                  <div className="hf-kpi" style={{ padding: '0.75rem', borderLeft: '3px solid #FBBF24' }}>
                    <div className="hf-kpi-value" style={{ fontSize: '1.5rem', color: '#FBBF24' }}>{proximityCoverage.atRiskCount}</div>
                    <div className="hf-kpi-label" style={{ fontSize: '0.75rem' }}>Closing</div>
                    <div className="hf-kpi-sub">Nearby supervisor near capacity</div>
                  </div>
                  <div className="hf-kpi" style={{ padding: '0.75rem', borderLeft: '3px solid #EF4444' }}>
                    <div className="hf-kpi-value" style={{ fontSize: '1.5rem', color: '#EF4444' }}>{proximityCoverage.uncoveredCount}</div>
                    <div className="hf-kpi-label" style={{ fontSize: '0.75rem' }}>Uncovered</div>
                    <div className="hf-kpi-sub">No supervisor within 45 min</div>
                  </div>
                  <div className="hf-kpi" style={{ padding: '0.75rem' }}>
                    <div className="hf-kpi-value" style={{ fontSize: '1.5rem' }}>{risk.supCount}</div>
                    <div className="hf-kpi-label" style={{ fontSize: '0.75rem' }}>Supervisors</div>
                    <div className="hf-kpi-sub">{risk.totalRemaining} slots remaining of {risk.totalMax}</div>
                  </div>
                </div>

                {/* Coverage bar */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={{ height: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '5px', overflow: 'hidden', display: 'flex' }}>
                    <div style={{ width: `${coveredPct}%`, background: '#4ADE80', transition: 'width 0.3s' }}></div>
                    <div style={{ width: `${atRiskPct}%`, background: '#FBBF24', transition: 'width 0.3s' }}></div>
                    <div style={{ width: `${uncoveredPct}%`, background: '#EF4444', transition: 'width 0.3s' }}></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.35rem', fontSize: '0.7rem', color: 'rgba(199, 210, 254, 0.5)' }}>
                    <span>{coveredPct.toFixed(0)}% covered</span>
                    {proximityCoverage.ungeocodedCandidates > 0 && (
                      <span>{proximityCoverage.ungeocodedCandidates} candidates not mapped (city not geocoded)</span>
                    )}
                    <span>{uncoveredPct.toFixed(0)}% uncovered</span>
                  </div>
                </div>

                {/* Uncovered cities — the actionable list */}
                {proximityCoverage.uncovered.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#EF4444', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#EF4444', display: 'inline-block' }}></span>
                      Uncovered Candidates — No Supervisor Within 45 Min
                    </div>
                    <div style={{ fontSize: '0.8rem', maxHeight: '200px', overflowY: 'auto', background: 'rgba(239, 68, 68, 0.05)', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(239, 68, 68, 0.1)' }}>
                        <span>City</span>
                        <span style={{ textAlign: 'center' }}>Candidates</span>
                      </div>
                      {proximityCoverage.uncovered.map((city, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px', padding: '0.4rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                          <span style={{ color: 'rgba(199, 210, 254, 0.9)' }}>{city.name}</span>
                          <span style={{ textAlign: 'center', color: '#EF4444', fontWeight: 700 }}>{city.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* At-risk cities */}
                {proximityCoverage.atRisk.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#FBBF24', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FBBF24', display: 'inline-block' }}></span>
                      Closing Capacity — Nearby Supervisors Nearing Limits
                    </div>
                    <div style={{ fontSize: '0.8rem', maxHeight: '200px', overflowY: 'auto', background: 'rgba(251, 191, 36, 0.05)', borderRadius: '6px', border: '1px solid rgba(251, 191, 36, 0.15)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 1fr', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(251, 191, 36, 0.1)' }}>
                        <span>City</span>
                        <span style={{ textAlign: 'center' }}>Candidates</span>
                        <span>Nearby Supervisors</span>
                      </div>
                      {proximityCoverage.atRisk.map((city, i) => (
                        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 1fr', padding: '0.4rem 0.75rem', borderBottom: '1px solid rgba(255,255,255,0.03)', alignItems: 'center' }}>
                          <span style={{ color: 'rgba(199, 210, 254, 0.9)' }}>{city.name}</span>
                          <span style={{ textAlign: 'center', color: '#FBBF24', fontWeight: 700 }}>{city.count}</span>
                          <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.75rem' }}>{city.nearbySupNames?.slice(0, 2).join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supervisor roster */}
                <div>
                  <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'rgba(199, 210, 254, 0.8)', marginBottom: '0.5rem' }}>
                    All Supervisors in {selectedState}
                  </div>
                  <div style={{ fontSize: '0.8rem', maxHeight: '250px', overflowY: 'auto' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 70px 50px 100px', padding: '0.5rem 0.75rem', color: 'rgba(199, 210, 254, 0.5)', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'sticky', top: 0, background: 'rgba(15, 23, 42, 0.95)' }}>
                      <span>Supervisor</span>
                      <span style={{ textAlign: 'center' }}>Assigned</span>
                      <span style={{ textAlign: 'center' }}>Max</span>
                      <span style={{ textAlign: 'center' }}>Open</span>
                      <span style={{ textAlign: 'center' }}></span>
                      <span>City</span>
                    </div>
                    {risk.supervisors
                      .sort((a, b) => a.remaining - b.remaining)
                      .map((sup, i) => (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 70px 70px 50px 100px', padding: '0.35rem 0.75rem', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                        <span style={{ color: 'rgba(199, 210, 254, 0.9)', fontSize: '0.78rem' }}>{sup.name}</span>
                        <span style={{ textAlign: 'center', color: 'rgba(199, 210, 254, 0.7)' }}>{sup.currentLoad}</span>
                        <span style={{ textAlign: 'center', color: 'rgba(199, 210, 254, 0.7)' }}>{sup.maxCapacity}</span>
                        <span style={{ textAlign: 'center', fontWeight: 700, color: riskColors[sup.capacityStatus] }}>{sup.remaining}</span>
                        <span style={{ textAlign: 'center' }}>
                          <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: riskColors[sup.capacityStatus] }}></span>
                        </span>
                        <span style={{ color: 'rgba(199, 210, 254, 0.5)', fontSize: '0.72rem' }}>{sup.city}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })() : (
            <div style={{ color: 'rgba(199, 210, 254, 0.5)', textAlign: 'center', padding: '1.5rem' }}>
              No supervisor data available for {selectedState}
            </div>
          )}
        </div>
      )}

      {/* State-level summary */}
      <div className="hf-states-card">
        <h3 className="hf-section-title">
          <Users size={18} />
          Eligible Candidates by State
        </h3>
        <div className="hf-states-grid">
          {stats?.stateBreakdown?.map(([state, count], i) => {
            const pct = stats.totalMatched > 0 ? (count / stats.totalMatched * 100) : 0;
            return (
              <div key={state} className="hf-state-row" 
                onClick={() => handleStateClick(state, count)}
                style={{ cursor: 'pointer', background: state === selectedState ? 'rgba(0, 190, 168, 0.1)' : 'transparent' }}
              >
                <span className="hf-state-name">{STATE_GEO[state]?.abbr || state}</span>
                <span className="hf-state-count">{count}</span>
                <div className="hf-state-bar-wrapper">
                  <div className="hf-state-bar" style={{ width: `${pct}%` }}></div>
                </div>
                <span className="hf-state-pct">{pct.toFixed(1)}%</span>
              </div>
            );
          })}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default HiringForecast;
